import { api } from "./client";
import type {
  AccountChange,
  AdminRoleName,
  AdministratorListPage,
  CreatedAdministrator,
  PasswordReset,
} from "./types";

/** Administrator accounts, their roles and their sessions.
 *
 * ONE FUNCTION PER OPERATION, mirroring the backend's explicit commands. There
 * is deliberately no `update(adminId, patch)`: a single call taking a bag of
 * fields is how a role ends up being set by something that meant to change a
 * display name, and the server does not offer one either.
 *
 * EVERY MUTATION CARRIES THE ACTING ADMINISTRATOR'S OWN PASSWORD, in the body,
 * for the life of one request. Never in the query string, never in a header,
 * never in storage: those are logged by proxies, kept in browser history and
 * echoed in referrers.
 *
 * There is no `remove`. Nothing in this system hard-deletes an administrator —
 * they are the actor on audit rows and stock movements — so `disable` is the
 * end of the lifecycle and the account stays visible.
 */

export interface UsersListQuery {
  search?: string;
  status?: "active" | "disabled";
  role?: AdminRoleName;
  page?: number;
  page_size?: number;
}

const ROOT = "/admin/users";

export const usersApi = {
  list: (query: UsersListQuery, signal?: AbortSignal) =>
    api.get<AdministratorListPage>(ROOT, { ...query }, signal),

  create: (body: {
    email: string;
    display_name: string;
    current_password: string;
  }) => api.post<CreatedAdministrator>(ROOT, body),

  grantRole: (adminId: string, role: AdminRoleName, currentPassword: string) =>
    api.post<AccountChange>(`${ROOT}/${adminId}/roles/${role}/grant`, {
      current_password: currentPassword,
    }),

  revokeRole: (adminId: string, role: AdminRoleName, currentPassword: string) =>
    api.post<AccountChange>(`${ROOT}/${adminId}/roles/${role}/revoke`, {
      current_password: currentPassword,
    }),

  disable: (adminId: string, currentPassword: string) =>
    api.post<AccountChange>(`${ROOT}/${adminId}/disable`, {
      current_password: currentPassword,
    }),

  reactivate: (adminId: string, currentPassword: string) =>
    api.post<AccountChange>(`${ROOT}/${adminId}/reactivate`, {
      current_password: currentPassword,
    }),

  resetPassword: (adminId: string, currentPassword: string) =>
    api.post<PasswordReset>(`${ROOT}/${adminId}/reset-password`, {
      current_password: currentPassword,
    }),
};
